import { logger } from '../../../shared/utils/logger';

// Raw IndexedDB on the main thread. Version and stores must match the legacy package's
// `indexDBConfig`; do not bump DB_VERSION. See .claude/context/persistence.md.
const DB_NAME = 'assistantBotDB';
const DB_VERSION = 2;

const STORES = {
  MESSAGES: 'messages',
  SESSIONS: 'sessions',
};

export const DB_CONFIG = {
  DB_NAME,
  DB_VERSION,
  STORES
};

/* ---- Schema and connection ---- */

const STORE_SCHEMAS = {
  [STORES.MESSAGES]: { keyPath: 'id', autoIncrement: true },
  [STORES.SESSIONS]: { keyPath: 'sessionId' },
};

const hasAllStores = (db) => Object.keys(STORE_SCHEMAS).every((name) => db.objectStoreNames.contains(name));

// Runs only when an open creates or upgrades the database. Adds a missing store and keeps the rest.
const createMissingStores = (db) => {
  Object.entries(STORE_SCHEMAS).forEach(([name, options]) => {
    if (!db.objectStoreNames.contains(name)) db.createObjectStore(name, options);
  });
};

const openConnection = () => new Promise((resolve, reject) => {
  const request = indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => createMissingStores(request.result);
  request.onsuccess = () => {
    const db = request.result;
    // Never block another opener's upgrade or delete.
    db.onversionchange = () => db.close();
    resolve(db);
  };
  request.onerror = () => reject(request.error);
});

// Resolves the connection if it has every store; otherwise closes it and resolves null.
const openIfComplete = async () => {
  const db = await openConnection();
  if (hasAllStores(db)) return db;
  db.close();
  return null;
};

const deleteDatabase = () => new Promise((resolve, reject) => {
  const request = indexedDB.deleteDatabase(DB_NAME);
  request.onsuccess = () => resolve();
  request.onerror = () => reject(request.error);
});

// Without the lock, two tabs can both delete, and the second delete erases the first tab's rebuild.
const withRebuildLock = (rebuild) => (
  typeof navigator !== 'undefined' && navigator.locks
    ? navigator.locks.request(`${DB_NAME}:repair`, rebuild)
    : rebuild()
);

// A database already at DB_VERSION gets no upgrade, so a missing store is never created: rebuild it.
// See .claude/context/persistence.md ("Repairing a database with no stores").
const openConnectionWithAllStores = async () => {
  const db = await openIfComplete();
  if (db) return db;

  return withRebuildLock(async () => {
    // Another tab may have rebuilt the database while this one waited for the lock.
    const rebuiltByAnotherTab = await openIfComplete();
    if (rebuiltByAnotherTab) return rebuiltByAnotherTab;

    await deleteDatabase();
    const rebuilt = await openIfComplete();
    if (rebuilt) return rebuilt;
    throw new DOMException(`${DB_NAME} has no object stores after a rebuild`, 'NotFoundError');
  });
};

// One cached connection, dropped whenever it stops being usable.
// See .claude/context/persistence.md ("Connection lifecycle").
let dbPromise = null;

const forgetConnection = (connection) => {
  if (dbPromise === connection) dbPromise = null;
};

/** Open (or reuse) the IndexedDB connection. Resolves with an `IDBDatabase` that has every store. */
export const openDatabase = () => {
  if (dbPromise) return dbPromise;

  const connection = openConnectionWithAllStores();
  dbPromise = connection;
  /* eslint-disable no-param-reassign */
  connection.then((db) => {
    db.onclose = () => {
      logger.warn('[IDB] connection closed by the browser');
      forgetConnection(connection);
    };
    // Replaces openConnection's handler: a script's own close() fires no `close` event.
    db.onversionchange = () => {
      db.close();
      logger.warn('[IDB] connection closed for another opener');
      forgetConnection(connection);
    };
  }, (error) => {
    logger.error('[IDB] open failed:', error);
    forgetConnection(connection);
  });
  return connection;
};

/**
 * Checks, and if a store is missing rebuilds, the database at page load, before either journey reads
 * it. Uses its own connection and closes it, so it never touches the cache that `openDatabase` owns.
 * Resolves true when every store exists, false otherwise (including where there is no IndexedDB).
 */
export const initDBConnection = async () => {
  if (typeof indexedDB === 'undefined') return false;
  try {
    const db = await openConnectionWithAllStores();
    // Self-cleaning, though correctness does not need it: the connection would close on
    // `versionchange` anyway. Nothing on the legacy journey would ever use it.
    db.close();
    return true;
  } catch (error) {
    logger.error('[IDB] startup repair failed:', error);
    return false;
  }
};

/* ---- Retry ---- */

// See .claude/context/persistence.md ("Which failures are retried").
const canRetry = (error, { idempotent }) => {
  if (error?.name === 'InvalidStateError' || error?.name === 'AbortError') return true;
  return error?.name === 'UnknownError' && idempotent;
};

// Runs `op(db)`; if the connection is dead, forgets it and runs once more when `canRetry` allows.
const doIDBOperationWithRetry = ({ idempotent }, op) => {
  const connection = openDatabase();
  return connection.then(op).catch((error) => {
    const retry = canRetry(error, { idempotent });
    logger.error(`[IDB] operation failed, ${retry ? 'retrying once' : 'not retried'}:`, error);
    if (!retry) throw error;
    forgetConnection(connection);
    return openDatabase().then(op);
  });
};

/* ---- Operations ---- */

const requestResult = (request) => new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

// Resolves when the transaction commits: a write is durable only then. `onabort` is load-bearing —
// see .claude/context/persistence.md ("Connection lifecycle").
/* eslint-disable no-param-reassign */
const transactionComplete = (tx) => new Promise((resolve, reject) => {
  tx.oncomplete = () => resolve();
  // A request's error reaches here before the transaction aborts, so `tx.error` is still null.
  tx.onerror = (event) => reject(event.target.error ?? tx.error);
  tx.onabort = () => reject(tx.error ?? new DOMException('Transaction aborted', 'AbortError'));
});
/* eslint-enable no-param-reassign */

// A put that carries its key replaces one row, so running it twice is safe; a keyless put inserts.
const hasKey = (storeName, record) => record?.[STORE_SCHEMAS[storeName]?.keyPath] !== undefined;

/** Read every record in `storeName`. Resolves with an array (never null). */
export const getAll = (storeName) => doIDBOperationWithRetry({ idempotent: true }, async (db) => {
  const request = db.transaction(storeName, 'readonly').objectStore(storeName).getAll();
  const records = await requestResult(request);
  return records || [];
});

/** Insert a brand-new record. Rejects if a record with the same key exists. */
export const add = (storeName, record) => doIDBOperationWithRetry({ idempotent: false }, async (db) => {
  const tx = db.transaction(storeName, 'readwrite');
  tx.objectStore(storeName).add(record);
  await transactionComplete(tx);
  return record;
});

/** Insert-or-replace a record (IndexedDB `put`). Used for updates. */
export const update = (storeName, record) => doIDBOperationWithRetry(
  { idempotent: hasKey(storeName, record) },
  async (db) => {
    const tx = db.transaction(storeName, 'readwrite');
    tx.objectStore(storeName).put(record);
    await transactionComplete(tx);
    return record;
  },
);

/** Alias of `update`. */
export const put = update;

/** Read one record by key. Resolves with the record or `null` if absent. */
export const get = (storeName, key) => doIDBOperationWithRetry({ idempotent: true }, async (db) => {
  const request = db.transaction(storeName, 'readonly').objectStore(storeName).get(key);
  const record = await requestResult(request);
  return record ?? null;
});

/** Add `record` unless a row matches it on `matchField`; resolves true if inserted. One
 * `readwrite` transaction, so it is cross-tab safe — see .claude/context/persistence.md. */
export const insertIfAbsent = (storeName, record, matchField) => doIDBOperationWithRetry(
  { idempotent: true },
  async (db) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    let inserted = false;

    store.openCursor().onsuccess = (event) => {
      const cursor = event.target.result;
      if (!cursor) {
        store.add(record);
        inserted = true;
        return;
      }
      // Match: stop walking, and the transaction commits with no change.
      if (cursor.value?.[matchField] === record[matchField]) return;
      cursor.continue();
    };

    await transactionComplete(tx);
    return inserted;
  },
);

/** Delete messages. `keepInitial` (default true) keeps the `isInitialMessage` row. */
export const clearMessages = ({ keepInitial = true } = {}) => doIDBOperationWithRetry(
  { idempotent: true },
  async (db) => {
    const tx = db.transaction(STORES.MESSAGES, 'readwrite');

    tx.objectStore(STORES.MESSAGES).openCursor().onsuccess = (event) => {
      const cursor = event.target.result;
      if (!cursor) return;
      const isInitial = cursor.value?.isInitialMessage === true;
      if (!(keepInitial && isInitial)) cursor.delete();
      cursor.continue();
    };

    await transactionComplete(tx);
    return true;
  },
);

/** Startup prune: keep the first initial message, delete duplicate initials and other
 * sessions' rows, and blank the kept initial's suggested questions for b2b customers. */
export const cleanOrphans = ({ sessionId, customerType }) => doIDBOperationWithRetry(
  { idempotent: true },
  async (db) => {
    const tx = db.transaction(STORES.MESSAGES, 'readwrite');
    let initialMessageFound = false;

    tx.objectStore(STORES.MESSAGES).openCursor().onsuccess = (event) => {
      const cursor = event.target.result;
      if (!cursor) return;

      const value = cursor.value;
      const isDuplicateInitial = value.isInitialMessage && initialMessageFound;

      if (value.isInitialMessage && !initialMessageFound) {
        initialMessageFound = true;
        const hasSuggested = value.content?.relatedQuestions?.suggestedQuestions?.length;
        if (customerType === 'b2b' && hasSuggested) {
          cursor.update({
            ...value,
            content: {
              ...value.content,
              relatedQuestions: {
                suggestedQuestions: [],
                followUpQuestions: [],
                clarifyingQuestions: [],
              },
            },
            updatedAt: Date.now(),
          });
        }
      }

      if (value.sessionId !== sessionId || isDuplicateInitial) cursor.delete();
      cursor.continue();
    };

    await transactionComplete(tx);
    return true;
  },
);
